// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/lib/use-zod-form.ts
import { useCallback, useMemo, useState } from "react";
function useZodForm(schema, initial) {
  const baseline = useMemo(() => initial, [initial]);
  const [values, setValues] = useState(baseline);
  const [errors, setErrors] = useState({});
  const setField = useCallback((key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!(key in current)) return current;
      const { [key]: _drop, ...rest } = current;
      return rest;
    });
  }, []);
  const patch = useCallback((next) => {
    setValues((current) => ({ ...current, ...next }));
  }, []);
  const reset = useCallback(
    (next) => {
      setValues({ ...baseline, ...next ?? {} });
      setErrors({});
    },
    [baseline]
  );
  const validate = useCallback(() => {
    const result = schema.safeParse(values);
    if (result.success) {
      setErrors({});
      return { ok: true, data: result.data };
    }
    const fieldErrors = zodErrorToFieldMap(result.error);
    setErrors(fieldErrors);
    return { ok: false, errors: fieldErrors };
  }, [schema, values]);
  const validateField = useCallback(
    (key) => {
      const result = schema.safeParse(values);
      const message = result.success ? void 0 : zodErrorToFieldMap(result.error)[key];
      setErrors((current) => {
        if (message) return { ...current, [key]: message };
        if (!(key in current)) return current;
        const { [key]: _drop, ...rest } = current;
        return rest;
      });
    },
    [schema, values]
  );
  const clearError = useCallback((key) => {
    setErrors((current) => {
      if (!(key in current)) return current;
      const { [key]: _drop, ...rest } = current;
      return rest;
    });
  }, []);
  const setServerErrors = useCallback((next) => {
    setErrors(next);
  }, []);
  return {
    values,
    errors,
    setField,
    patch,
    reset,
    validate,
    validateField,
    clearError,
    setServerErrors
  };
}
function zodErrorToFieldMap(error) {
  const out = {};
  for (const issue of error.issues) {
    const head = issue.path[0];
    if (typeof head === "string" && !(head in out)) {
      out[head] = issue.message;
    }
  }
  return out;
}

// src/lib/sla.ts
var WARN_MS = 5 * 60 * 1e3;
function slaState(dueAt, now = /* @__PURE__ */ new Date()) {
  if (!dueAt) return "fresh";
  const due = new Date(dueAt);
  if (!isFinite(due.getTime())) return "fresh";
  const msLeft = due.getTime() - now.getTime();
  if (msLeft <= 0) return "breach";
  if (msLeft <= WARN_MS) return "warn";
  return "fresh";
}
function msUntilDue(dueAt, now = /* @__PURE__ */ new Date()) {
  if (!dueAt) return 0;
  const due = new Date(dueAt);
  if (!isFinite(due.getTime())) return 0;
  return due.getTime() - now.getTime();
}
function formatMss(ms) {
  const absMs = Math.abs(ms);
  const totalSeconds = Math.floor(absMs / 1e3);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

// src/lib/score.ts
function scoreBand(score) {
  const clamped = Math.max(0, Math.min(100, score));
  if (clamped < 40) return "cold";
  if (clamped < 70) return "warm";
  return "hot";
}
function clampScore(score) {
  return Math.max(0, Math.min(100, score));
}

// src/components/badge.tsx
import { cva } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
var badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-transparent bg-destructive text-destructive-foreground",
        // Monochrome: success uses the strongest fill; warning is outlined for
        // a clearly different shape without introducing color.
        success: "border-transparent bg-success text-success-foreground",
        warning: "border-foreground/40 bg-warning text-warning-foreground",
        outline: "border-border text-foreground"
      }
    },
    defaultVariants: {
      variant: "outline"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn(badgeVariants({ variant }), className), ...props });
}

// src/components/bottom-nav.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function BottomNav({
  className,
  children
}) {
  return /* @__PURE__ */ jsx2(
    "nav",
    {
      "aria-label": "Primary",
      className: cn(
        "fixed inset-x-0 bottom-0 z-30 border-t bg-background pb-safe md:hidden",
        className
      ),
      children: /* @__PURE__ */ jsx2("ul", { className: "grid h-bottom-nav grid-flow-col auto-cols-fr items-stretch", children })
    }
  );
}
function BottomNavItem({
  active,
  icon,
  label,
  children,
  className
}) {
  return /* @__PURE__ */ jsx2("li", { className: "contents", children: /* @__PURE__ */ jsx2(
    "div",
    {
      className: cn(
        "relative flex h-full min-w-touch items-center justify-center",
        className
      ),
      children: children ?? /* @__PURE__ */ jsx2(BottomNavLabel, { active, icon, label })
    }
  ) });
}
function BottomNavLabel({
  active,
  icon,
  label
}) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "flex h-full w-full flex-col items-center justify-center gap-1 text-[10px] font-medium uppercase tracking-wider transition-colors",
        active ? "text-foreground" : "text-muted-foreground"
      ),
      children: [
        /* @__PURE__ */ jsx2(
          "span",
          {
            className: cn(
              "flex h-6 w-6 items-center justify-center",
              active ? "" : "opacity-80"
            ),
            children: icon
          }
        ),
        /* @__PURE__ */ jsx2("span", { className: "truncate", children: label })
      ]
    }
  );
}
function BottomNavSpacer({ className }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      "aria-hidden": true,
      className: cn("h-bottom-nav pb-safe md:hidden", className)
    }
  );
}

// src/components/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx3 } from "react/jsx-runtime";
var buttonVariants = cva2(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        // Touch-first sizing: ≥44px on phones, compact at sm+ to keep the
        // existing dense desktop feel.
        default: "h-11 px-4 py-2 sm:h-9",
        sm: "h-10 rounded-md px-3 text-xs sm:h-8",
        lg: "h-12 rounded-md px-6 sm:h-10",
        icon: "h-11 w-11 sm:h-9 sm:w-9"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx3(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/calendar/calendar.tsx
import * as React4 from "react";

// src/lib/date.ts
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function addMonths(d, n) {
  const x = new Date(d);
  const day = x.getDate();
  x.setDate(1);
  x.setMonth(x.getMonth() + n);
  const lastDay = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
  x.setDate(Math.min(day, lastDay));
  return x;
}
function startOfWeek(d, weekStartsOn = 0) {
  const x = startOfDay(d);
  const diff = (x.getDay() - weekStartsOn + 7) % 7;
  return addDays(x, -diff);
}
function endOfWeek(d, weekStartsOn = 0) {
  return endOfDay(addDays(startOfWeek(d, weekStartsOn), 6));
}
function startOfMonth(d) {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function isSameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}
function isToday(d) {
  return isSameDay(d, /* @__PURE__ */ new Date());
}
function minutesIntoDay(d) {
  return d.getHours() * 60 + d.getMinutes();
}
function monthGridDays(month, weekStartsOn = 0) {
  const gridStart = startOfWeek(startOfMonth(month), weekStartsOn);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}
function formatMonthYear(d) {
  return d.toLocaleDateString(void 0, { month: "long", year: "numeric" });
}
function formatWeekdayShort(d) {
  return d.toLocaleDateString(void 0, { weekday: "short" });
}
function formatDayHeading(d) {
  return d.toLocaleDateString(void 0, {
    weekday: "long",
    month: "long",
    day: "numeric"
  });
}
function formatHourLabel(hour) {
  const d = /* @__PURE__ */ new Date();
  d.setHours(hour, 0, 0, 0);
  return d.toLocaleTimeString(void 0, { hour: "numeric" });
}
function formatTime(d) {
  return d.toLocaleTimeString(void 0, { hour: "numeric", minute: "2-digit" });
}
function formatWeekRange(start, end) {
  const monthDay = { month: "short", day: "numeric" };
  if (start.getFullYear() !== end.getFullYear()) {
    const withYear = { ...monthDay, year: "numeric" };
    return `${start.toLocaleDateString(void 0, withYear)} \u2013 ${end.toLocaleDateString(void 0, withYear)}`;
  }
  const startStr = start.toLocaleDateString(void 0, monthDay);
  const endStr = end.toLocaleDateString(
    void 0,
    start.getMonth() === end.getMonth() ? { day: "numeric" } : monthDay
  );
  return `${startStr} \u2013 ${endStr}, ${end.getFullYear()}`;
}

// src/components/calendar/calendar-toolbar.tsx
import { ChevronLeft, ChevronRight } from "lucide-react";
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var VIEWS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" }
];
function CalendarToolbar({
  title,
  view,
  onViewChange,
  onPrev,
  onNext,
  onToday
}) {
  return /* @__PURE__ */ jsxs2("div", { className: "flex flex-wrap items-center gap-3 border-b border-border px-1 pb-3", children: [
    /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1", children: [
      /* @__PURE__ */ jsx4(
        Button,
        {
          variant: "outline",
          size: "icon",
          onClick: onPrev,
          "aria-label": "Previous period",
          children: /* @__PURE__ */ jsx4(ChevronLeft, {})
        }
      ),
      /* @__PURE__ */ jsx4(
        Button,
        {
          variant: "outline",
          size: "icon",
          onClick: onNext,
          "aria-label": "Next period",
          children: /* @__PURE__ */ jsx4(ChevronRight, {})
        }
      ),
      /* @__PURE__ */ jsx4(Button, { variant: "outline", size: "sm", onClick: onToday, className: "ml-1", children: "Today" })
    ] }),
    /* @__PURE__ */ jsx4("h2", { className: "order-last w-full text-base font-semibold text-foreground sm:order-none sm:w-auto sm:flex-1", children: title }),
    /* @__PURE__ */ jsx4(
      "div",
      {
        role: "group",
        "aria-label": "Calendar view",
        className: "inline-flex shrink-0 items-center rounded-md bg-muted p-0.5",
        children: VIEWS.map((v) => {
          const active = v.value === view;
          return /* @__PURE__ */ jsx4(
            "button",
            {
              type: "button",
              "aria-pressed": active,
              onClick: () => onViewChange(v.value),
              className: cn(
                "rounded-sm px-3 py-1 text-sm font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              ),
              children: v.label
            },
            v.value
          );
        })
      }
    )
  ] });
}

// src/components/calendar/calendar-month-view.tsx
import * as React2 from "react";

// src/components/channel-icon.tsx
import {
  Bot,
  FileText,
  Globe,
  Instagram,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Send,
  Video
} from "lucide-react";
import { jsx as jsx5 } from "react/jsx-runtime";
var CHANNEL_VISUAL = {
  sms: { Icon: MessageSquare, colorClass: "text-[#0E9F6E]", label: "SMS" },
  email: { Icon: Mail, colorClass: "text-[#2563EB]", label: "Email" },
  voice: { Icon: Phone, colorClass: "text-[#0F7A8C]", label: "Voice" },
  video: { Icon: Video, colorClass: "text-[#7C3AED]", label: "Video" },
  web: { Icon: Globe, colorClass: "text-[#7C3AED]", label: "Web chat" },
  web_form: { Icon: FileText, colorClass: "text-[#7C3AED]", label: "Web form" },
  messenger: { Icon: MessageCircle, colorClass: "text-[#1877F2]", label: "Messenger" },
  instagram: { Icon: Instagram, colorClass: "text-[#E1306C]", label: "Instagram" },
  whatsapp: { Icon: MessageCircle, colorClass: "text-[#25D366]", label: "WhatsApp" },
  telegram: { Icon: Send, colorClass: "text-sky-500", label: "Telegram" },
  aap: { Icon: Bot, colorClass: "text-slate-500", label: "Auto Agent Protocol" }
};
var FALLBACK = { Icon: MessageCircle, colorClass: "text-muted-foreground", label: "Unknown" };
function ChannelIcon({ channel, size = "sm", className }) {
  const v = CHANNEL_VISUAL[channel] ?? FALLBACK;
  const Icon2 = v.Icon;
  return /* @__PURE__ */ jsx5(
    Icon2,
    {
      className: cn(
        size === "xs" ? "h-3 w-3" : "h-4 w-4",
        v.colorClass,
        className
      ),
      "aria-label": v.label,
      role: "img"
    }
  );
}
function channelLabel(channel) {
  return (CHANNEL_VISUAL[channel] ?? FALLBACK).label;
}

// src/components/calendar/calendar-event.tsx
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var TONE = {
  default: {
    dot: "bg-primary",
    ring: "ring-primary",
    fill: "bg-primary text-primary-foreground",
    bar: "bg-primary"
  },
  success: {
    dot: "bg-success",
    ring: "ring-success",
    fill: "bg-success text-success-foreground",
    bar: "bg-success"
  },
  warning: {
    dot: "bg-warning border border-foreground/40",
    ring: "ring-warning",
    fill: "bg-warning text-warning-foreground border border-foreground/20",
    bar: "bg-warning"
  },
  danger: {
    dot: "bg-destructive",
    ring: "ring-destructive",
    fill: "bg-destructive text-destructive-foreground",
    bar: "bg-destructive"
  },
  muted: {
    dot: "bg-muted-foreground/40",
    ring: "ring-muted-foreground/40",
    fill: "bg-muted text-muted-foreground",
    bar: "bg-muted-foreground/40"
  }
};
function toneOf(event) {
  const key = event.colorKey ?? event.tone ?? "default";
  return TONE[key] ?? TONE.default;
}
function MonthEventChip({
  event,
  onClick
}) {
  const tone = toneOf(event);
  return /* @__PURE__ */ jsxs3(
    "button",
    {
      type: "button",
      title: `${formatTime(event.start)} \xB7 ${event.title}`,
      onClick: (e) => {
        e.stopPropagation();
        onClick?.(event);
      },
      className: cn(
        "flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-[11px] leading-tight",
        "transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      ),
      children: [
        event.channel ? /* @__PURE__ */ jsx6(ChannelIcon, { channel: event.channel, size: "xs", className: "shrink-0" }) : /* @__PURE__ */ jsx6(
          "span",
          {
            className: cn(
              "h-1.5 w-1.5 shrink-0 rounded-full",
              event.kind === "task" ? cn("bg-background ring-2 ring-inset", tone.ring) : tone.dot
            )
          }
        ),
        /* @__PURE__ */ jsx6("span", { className: "shrink-0 tabular-nums text-muted-foreground", children: formatTime(event.start) }),
        /* @__PURE__ */ jsx6("span", { className: "truncate font-medium text-foreground", children: event.title })
      ]
    }
  );
}
function TimedEventBlock({
  event,
  style,
  onClick
}) {
  const tone = toneOf(event);
  const heightPx = typeof style.height === "number" ? style.height : 48;
  const compact = heightPx < 44;
  const isTask = event.kind === "task";
  const range = event.end ? `${formatTime(event.start)} \u2013 ${formatTime(event.end)}` : formatTime(event.start);
  const detailParts = [range];
  if (event.vehicleLabel) detailParts.push(event.vehicleLabel);
  if (event.channel) detailParts.push(channelLabel(event.channel));
  const detail = detailParts.join(" \xB7 ");
  return /* @__PURE__ */ jsxs3(
    "button",
    {
      type: "button",
      style,
      title: `${range} \xB7 ${event.title}`,
      onClick: (e) => {
        e.stopPropagation();
        onClick?.(event);
      },
      className: cn(
        "absolute overflow-hidden rounded-md text-left transition-shadow",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "hover:z-10 hover:shadow-md",
        isTask ? "border border-dashed border-border bg-background" : tone.fill,
        compact ? "px-1.5 py-0.5" : "p-1.5"
      ),
      children: [
        isTask && /* @__PURE__ */ jsx6(
          "span",
          {
            className: cn("absolute inset-y-0 left-0 w-1 rounded-l-md", tone.bar)
          }
        ),
        /* @__PURE__ */ jsxs3("div", { className: cn("flex flex-col", isTask && "pl-1.5"), children: [
          /* @__PURE__ */ jsxs3(
            "span",
            {
              className: cn(
                "flex items-center gap-1 truncate font-semibold",
                compact ? "text-[11px] leading-tight" : "text-xs"
              ),
              children: [
                event.channel && !compact && /* @__PURE__ */ jsx6(ChannelIcon, { channel: event.channel, size: "xs", className: "shrink-0" }),
                event.title
              ]
            }
          ),
          !compact && /* @__PURE__ */ jsxs3(
            "span",
            {
              className: cn(
                "truncate text-[11px] leading-tight",
                isTask ? "text-muted-foreground" : "opacity-80"
              ),
              children: [
                detail,
                event.subtitle && !event.vehicleLabel && !event.channel ? ` \xB7 ${event.subtitle}` : ""
              ]
            }
          )
        ] })
      ]
    }
  );
}

// src/components/calendar/calendar-month-view.tsx
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var DEFAULT_SLOT_HOUR = 9;
var VISIBLE_PER_CELL = 3;
function dayKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function CalendarMonthView({
  focusDate,
  events,
  onEventClick,
  onSlotClick,
  onDateClick
}) {
  const days = React2.useMemo(() => monthGridDays(focusDate), [focusDate]);
  const byDay = React2.useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    for (const event of events) {
      const key = dayKey(event.start);
      const list = map.get(key);
      if (list) list.push(event);
      else map.set(key, [event]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.start.getTime() - b.start.getTime());
    }
    return map;
  }, [events]);
  return /* @__PURE__ */ jsxs4("div", { className: "flex h-full flex-col", children: [
    /* @__PURE__ */ jsx7("div", { className: "grid grid-cols-7 border-b border-border", children: days.slice(0, 7).map((d) => /* @__PURE__ */ jsx7(
      "div",
      {
        className: "px-2 py-1.5 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground",
        children: formatWeekdayShort(d)
      },
      d.getDay()
    )) }),
    /* @__PURE__ */ jsx7("div", { className: "grid flex-1 grid-cols-7 grid-rows-6", children: days.map((day) => {
      const inMonth = isSameMonth(day, focusDate);
      const today = isToday(day);
      const dayEvents = byDay.get(dayKey(day)) ?? [];
      const visible = dayEvents.length > VISIBLE_PER_CELL ? dayEvents.slice(0, VISIBLE_PER_CELL - 1) : dayEvents;
      const overflow = dayEvents.length - visible.length;
      return /* @__PURE__ */ jsxs4(
        "div",
        {
          onClick: () => {
            const slot = new Date(day);
            slot.setHours(DEFAULT_SLOT_HOUR, 0, 0, 0);
            onSlotClick?.(slot);
          },
          className: cn(
            "flex min-h-0 flex-col gap-0.5 border-b border-r border-border p-1",
            "transition-colors [&:nth-child(7n)]:border-r-0",
            onSlotClick && "cursor-pointer hover:bg-muted/50",
            !inMonth && "bg-muted/30"
          ),
          children: [
            /* @__PURE__ */ jsx7(
              "button",
              {
                type: "button",
                onClick: (e) => {
                  e.stopPropagation();
                  onDateClick(day);
                },
                className: cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center self-start rounded-full text-xs font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  today && "bg-primary text-primary-foreground",
                  !today && inMonth && "text-foreground hover:bg-muted",
                  !today && !inMonth && "text-muted-foreground hover:bg-muted"
                ),
                "aria-label": day.toDateString(),
                children: day.getDate()
              }
            ),
            /* @__PURE__ */ jsxs4("div", { className: "flex min-h-0 flex-col gap-0.5 overflow-hidden", children: [
              visible.map((event) => /* @__PURE__ */ jsx7(
                MonthEventChip,
                {
                  event,
                  onClick: onEventClick
                },
                event.id
              )),
              overflow > 0 && /* @__PURE__ */ jsxs4(
                "button",
                {
                  type: "button",
                  onClick: (e) => {
                    e.stopPropagation();
                    onDateClick(day);
                  },
                  className: "px-1 text-left text-[11px] font-medium text-muted-foreground hover:text-foreground",
                  children: [
                    "+",
                    overflow,
                    " more"
                  ]
                }
              )
            ] })
          ]
        },
        day.toISOString()
      );
    }) })
  ] });
}

// src/components/calendar/calendar-time-grid.tsx
import * as React3 from "react";

// src/components/calendar/calendar-layout.ts
var DAY_START_HOUR = 7;
var DAY_END_HOUR = 21;
var HOUR_HEIGHT = 112;
var VISIBLE_HOURS = 6;
var MIN_BLOCK_HEIGHT = 22;
var TASK_SPAN_MIN = 30;
var WINDOW_START_MIN = DAY_START_HOUR * 60;
var WINDOW_END_MIN = DAY_END_HOUR * 60;
var GRID_HEIGHT = (DAY_END_HOUR - DAY_START_HOUR) * HOUR_HEIGHT;
var HOURS = Array.from(
  { length: DAY_END_HOUR - DAY_START_HOUR },
  (_, i) => DAY_START_HOUR + i
);
var SALES_HOURS_BY_DAY = [
  null,
  // Sun — closed
  { startMinute: 480, endMinute: 1140 },
  // Mon 8:00 AM – 7:00 PM
  { startMinute: 480, endMinute: 1140 },
  // Tue
  { startMinute: 480, endMinute: 1140 },
  // Wed
  { startMinute: 480, endMinute: 1140 },
  // Thu
  { startMinute: 480, endMinute: 1140 },
  // Fri
  { startMinute: 540, endMinute: 1080 }
  // Sat 9:00 AM – 6:00 PM
];
var SERVICE_HOURS_BY_DAY = [
  null,
  // Sun — closed
  { startMinute: 420, endMinute: 1080 },
  // Mon 7:00 AM – 6:00 PM
  { startMinute: 420, endMinute: 1080 },
  // Tue
  { startMinute: 420, endMinute: 1080 },
  // Wed
  { startMinute: 420, endMinute: 1080 },
  // Thu
  { startMinute: 420, endMinute: 1080 },
  // Fri
  { startMinute: 480, endMinute: 840 }
  // Sat 8:00 AM – 2:00 PM
];
function minuteBandStyle(startMinute, endMinute) {
  const topMin = Math.max(startMinute, WINDOW_START_MIN);
  const botMin = Math.min(endMinute, WINDOW_END_MIN);
  return {
    top: (topMin - WINDOW_START_MIN) / 60 * HOUR_HEIGHT,
    height: Math.max(botMin - topMin, 0) / 60 * HOUR_HEIGHT
  };
}
function eventBounds(event) {
  const start = minutesIntoDay(event.start);
  const rawEnd = event.end ? minutesIntoDay(event.end) : start + TASK_SPAN_MIN;
  return [start, Math.min(Math.max(rawEnd, start + 15), 24 * 60)];
}
function layoutColumn(events) {
  const items = events.map((event) => {
    const [startMin, endMin] = eventBounds(event);
    return { event, startMin, endMin };
  }).sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
  const placed = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    if (cluster.length === 0) return;
    const laneEnds = [];
    const laneByItem = /* @__PURE__ */ new Map();
    for (const item of cluster) {
      let lane = laneEnds.findIndex((end) => end <= item.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(item.endMin);
      } else {
        laneEnds[lane] = item.endMin;
      }
      laneByItem.set(item, lane);
    }
    for (const item of cluster) {
      placed.push({
        event: item.event,
        startMin: item.startMin,
        endMin: item.endMin,
        lane: laneByItem.get(item) ?? 0,
        lanes: laneEnds.length
      });
    }
    cluster = [];
    clusterEnd = -1;
  };
  for (const item of items) {
    if (cluster.length > 0 && item.startMin >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.endMin);
  }
  flush();
  return placed;
}
function blockStyle(placed) {
  const topMin = Math.min(
    Math.max(placed.startMin, WINDOW_START_MIN),
    WINDOW_END_MIN
  );
  const botMin = Math.min(Math.max(placed.endMin, topMin), WINDOW_END_MIN);
  const height = Math.max(
    (botMin - topMin) / 60 * HOUR_HEIGHT,
    MIN_BLOCK_HEIGHT
  );
  const top = Math.min(
    (topMin - WINDOW_START_MIN) / 60 * HOUR_HEIGHT,
    GRID_HEIGHT - height
  );
  const widthPct = 100 / placed.lanes;
  return {
    top,
    height,
    left: `${placed.lane * widthPct}%`,
    width: `calc(${widthPct}% - 3px)`
  };
}

// src/components/calendar/calendar-time-grid.tsx
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
function CalendarTimeGrid({
  days,
  events,
  onEventClick,
  onSlotClick,
  onDateClick,
  focusDate
}) {
  const scrollRef = React3.useRef(null);
  const focusIndex = React3.useMemo(() => {
    const byFocus = focusDate ? days.findIndex((d) => isSameDay(d, focusDate)) : -1;
    if (byFocus >= 0) return byFocus;
    const byToday = days.findIndex((d) => isToday(d));
    return byToday >= 0 ? byToday : 0;
  }, [days, focusDate]);
  React3.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = Math.max(0, (9 - DAY_START_HOUR) * HOUR_HEIGHT - 12);
  }, []);
  const [now, setNow] = React3.useState(() => /* @__PURE__ */ new Date());
  React3.useEffect(() => {
    const t = window.setInterval(() => setNow(/* @__PURE__ */ new Date()), 6e4);
    return () => window.clearInterval(t);
  }, []);
  const hourLabels = React3.useMemo(
    () => HOURS.map((h) => formatHourLabel(h)),
    []
  );
  const nowMin = minutesIntoDay(now);
  const nowVisible = nowMin >= WINDOW_START_MIN && nowMin <= WINDOW_END_MIN;
  const nowTop = (nowMin - WINDOW_START_MIN) / 60 * HOUR_HEIGHT;
  return /* @__PURE__ */ jsxs5("div", { className: "flex h-full flex-col overflow-hidden", children: [
    /* @__PURE__ */ jsxs5("div", { className: "flex border-b border-border bg-background", children: [
      /* @__PURE__ */ jsx8("div", { className: "w-14 shrink-0" }),
      days.map((day, i) => {
        const today = isToday(day);
        return /* @__PURE__ */ jsxs5(
          "button",
          {
            type: "button",
            onClick: () => onDateClick?.(day),
            disabled: !onDateClick,
            className: cn(
              "flex-1 flex-col items-center gap-0.5 py-2",
              i === focusIndex ? "flex" : "hidden md:flex",
              onDateClick && "transition-colors hover:bg-muted"
            ),
            children: [
              /* @__PURE__ */ jsx8("span", { className: "text-xs font-medium uppercase tracking-wide text-muted-foreground", children: formatWeekdayShort(day) }),
              /* @__PURE__ */ jsx8(
                "span",
                {
                  className: cn(
                    "flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold",
                    today ? "bg-primary text-primary-foreground" : "text-foreground"
                  ),
                  children: day.getDate()
                }
              )
            ]
          },
          day.toISOString()
        );
      })
    ] }),
    /* @__PURE__ */ jsx8(
      "div",
      {
        ref: scrollRef,
        className: "flex-1 overflow-y-auto",
        style: { maxHeight: VISIBLE_HOURS * HOUR_HEIGHT },
        children: /* @__PURE__ */ jsxs5("div", { className: "flex", children: [
          /* @__PURE__ */ jsx8("div", { className: "w-14 shrink-0", children: HOURS.map((hour) => /* @__PURE__ */ jsx8(
            "div",
            {
              style: { height: HOUR_HEIGHT },
              className: "relative",
              children: /* @__PURE__ */ jsx8("span", { className: "absolute -top-2 right-2 text-[11px] tabular-nums text-muted-foreground", children: hour > DAY_START_HOUR ? hourLabels[hour - DAY_START_HOUR] : "" })
            },
            hour
          )) }),
          days.map((day, i) => {
            const dayEvents = events.filter((e) => isSameDay(e.start, day));
            const placed = layoutColumn(dayEvents);
            const dow = day.getDay();
            const salesBand = SALES_HOURS_BY_DAY[dow];
            const serviceBand = SERVICE_HOURS_BY_DAY[dow];
            const heading = formatDayHeading(day);
            return /* @__PURE__ */ jsxs5(
              "div",
              {
                className: cn(
                  "relative flex-1 border-l border-border",
                  i === focusIndex ? "block" : "hidden md:block"
                ),
                style: { height: GRID_HEIGHT },
                children: [
                  salesBand ? /* @__PURE__ */ jsx8(
                    "div",
                    {
                      className: "pointer-events-none absolute inset-x-0 bg-primary/[0.06]",
                      style: minuteBandStyle(
                        salesBand.startMinute,
                        salesBand.endMinute
                      ),
                      "aria-hidden": true
                    }
                  ) : null,
                  serviceBand ? /* @__PURE__ */ jsx8(
                    "div",
                    {
                      className: "pointer-events-none absolute inset-x-0 bg-primary/[0.06]",
                      style: minuteBandStyle(
                        serviceBand.startMinute,
                        serviceBand.endMinute
                      ),
                      "aria-hidden": true
                    }
                  ) : null,
                  HOURS.map((hour) => /* @__PURE__ */ jsx8(
                    "button",
                    {
                      type: "button",
                      disabled: !onSlotClick,
                      tabIndex: hour === DAY_START_HOUR ? 0 : -1,
                      "data-hour": hour,
                      onClick: () => {
                        const slot = new Date(day);
                        slot.setHours(hour, 0, 0, 0);
                        onSlotClick?.(slot);
                      },
                      onKeyDown: (e) => {
                        const delta = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
                        if (delta === 0) return;
                        e.preventDefault();
                        e.currentTarget.parentElement?.querySelector(
                          `button[data-hour="${hour + delta}"]`
                        )?.focus();
                      },
                      "aria-label": `New appointment ${heading} at ${hourLabels[hour - DAY_START_HOUR]}`,
                      style: { height: HOUR_HEIGHT },
                      className: cn(
                        "block w-full border-t border-border/60",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                        onSlotClick && "cursor-pointer hover:bg-muted/40"
                      )
                    },
                    hour
                  )),
                  nowVisible && isToday(day) && /* @__PURE__ */ jsxs5(
                    "div",
                    {
                      className: "pointer-events-none absolute inset-x-0 z-10 flex items-center",
                      style: { top: nowTop },
                      children: [
                        /* @__PURE__ */ jsx8("span", { className: "h-2 w-2 -translate-x-1 rounded-full bg-destructive" }),
                        /* @__PURE__ */ jsx8("span", { className: "h-px flex-1 bg-destructive" })
                      ]
                    }
                  ),
                  placed.map((p) => /* @__PURE__ */ jsx8(
                    TimedEventBlock,
                    {
                      event: p.event,
                      style: blockStyle(p),
                      onClick: onEventClick
                    },
                    p.event.id
                  ))
                ]
              },
              day.toISOString()
            );
          })
        ] })
      }
    )
  ] });
}

// src/components/calendar/calendar.tsx
import { jsx as jsx9, jsxs as jsxs6 } from "react/jsx-runtime";
function Calendar({
  events,
  view,
  focusDate,
  onViewChange,
  onFocusDateChange,
  onEventClick,
  onSlotClick,
  isLoading,
  className
}) {
  const title = view === "day" ? formatDayHeading(focusDate) : view === "week" ? formatWeekRange(startOfWeek(focusDate), endOfWeek(focusDate)) : formatMonthYear(focusDate);
  const weekDays = React4.useMemo(() => {
    const start = startOfWeek(focusDate);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [focusDate]);
  function step(direction) {
    if (view === "month") onFocusDateChange(addMonths(focusDate, direction));
    else if (view === "week")
      onFocusDateChange(addDays(focusDate, direction * 7));
    else onFocusDateChange(addDays(focusDate, direction));
  }
  function drillToDay(date) {
    onFocusDateChange(date);
    onViewChange("day");
  }
  function onKeyDown(e) {
    if (e.target !== e.currentTarget) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      step(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      step(1);
    } else if (e.key === "t" || e.key === "T") {
      e.preventDefault();
      onFocusDateChange(/* @__PURE__ */ new Date());
    }
  }
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      className: cn("flex h-full flex-col gap-3", className),
      tabIndex: 0,
      role: "group",
      "aria-label": "Calendar",
      onKeyDown,
      children: [
        /* @__PURE__ */ jsx9(
          CalendarToolbar,
          {
            title,
            view,
            onViewChange,
            onPrev: () => step(-1),
            onNext: () => step(1),
            onToday: () => onFocusDateChange(/* @__PURE__ */ new Date())
          }
        ),
        /* @__PURE__ */ jsx9("div", { className: "min-h-0 flex-1 overflow-hidden rounded-lg border border-border", children: isLoading ? /* @__PURE__ */ jsx9("div", { className: "flex h-full items-center justify-center p-8 text-sm text-muted-foreground", children: "Loading\u2026" }) : view === "month" ? /* @__PURE__ */ jsx9(
          CalendarMonthView,
          {
            focusDate,
            events,
            onEventClick,
            onSlotClick,
            onDateClick: drillToDay
          }
        ) : /* @__PURE__ */ jsx9(
          CalendarTimeGrid,
          {
            days: view === "week" ? weekDays : [focusDate],
            focusDate,
            events,
            onEventClick,
            onSlotClick,
            onDateClick: view === "week" ? drillToDay : void 0
          }
        ) })
      ]
    }
  );
}

// src/components/calendar/calendar.types.ts
var LEAD_STATUS_COLOR_KEY = {
  new: "default",
  working: "warning",
  appointment: "success",
  won: "success",
  lost: "muted"
};
var APPOINTMENT_STATUS_COLOR_KEY = {
  unconfirmed: "warning",
  pending_customer: "warning",
  confirmed: "default",
  checked_in: "default",
  in_service: "default",
  ready_for_pickup: "success",
  completed: "success",
  cancelled: "muted",
  no_show: "danger",
  rescheduled: "warning"
};

// src/components/card.tsx
import * as React5 from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
var Card = React5.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx10(
    "div",
    {
      ref,
      className: cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm",
        className
      ),
      ...props
    }
  )
);
Card.displayName = "Card";
var CardHeader = React5.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx10(
    "div",
    {
      ref,
      className: cn("flex flex-col space-y-1.5 p-6", className),
      ...props
    }
  )
);
CardHeader.displayName = "CardHeader";
var CardTitle = React5.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx10(
    "h3",
    {
      ref,
      className: cn(
        "text-base font-semibold leading-none tracking-tight",
        className
      ),
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = React5.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx10("p", { ref, className: cn("text-sm text-muted-foreground", className), ...props })
);
CardDescription.displayName = "CardDescription";
var CardContent = React5.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx10("div", { ref, className: cn("p-6 pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = React5.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx10(
    "div",
    {
      ref,
      className: cn("flex items-center p-6 pt-0", className),
      ...props
    }
  )
);
CardFooter.displayName = "CardFooter";

// src/components/dialog.tsx
import * as React6 from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { jsx as jsx11, jsxs as jsxs7 } from "react/jsx-runtime";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React6.forwardRef(({ className, children, preventDismiss, onInteractOutside, onEscapeKeyDown, ...props }, ref) => /* @__PURE__ */ jsxs7(DialogPortal, { children: [
  /* @__PURE__ */ jsx11(DialogOverlay, {}),
  /* @__PURE__ */ jsxs7(
    DialogPrimitive.Content,
    {
      ref,
      onInteractOutside: (event) => {
        if (preventDismiss) event.preventDefault();
        onInteractOutside?.(event);
      },
      onEscapeKeyDown: (event) => {
        if (preventDismiss) event.preventDefault();
        onEscapeKeyDown?.(event);
      },
      className: cn(
        // Mobile: bottom sheet that takes full width and up to 92dvh height.
        "fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] w-full flex-col gap-4 rounded-t-2xl border border-b-0 bg-background p-5 pb-safe-or-4 shadow-xl duration-200",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:slide-out-to-bottom-96 data-[state=open]:slide-in-from-bottom-96",
        // Tablet+ (sm:): pop back to centered card. We override the slide
        // distance to zero so the only animation at sm+ is the zoom/fade.
        "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[85vh] sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:border-b sm:p-6 sm:pb-6",
        "sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:slide-in-from-bottom-0",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsxs7(
          DialogPrimitive.Close,
          {
            "aria-label": "Close",
            className: "absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring sm:right-4 sm:top-4 sm:h-8 sm:w-8",
            children: [
              /* @__PURE__ */ jsx11(X, { className: "h-4 w-4" }),
              /* @__PURE__ */ jsx11("span", { className: "sr-only", children: "Close" })
            ]
          }
        )
      ]
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx11(
  "div",
  {
    className: cn("flex flex-col space-y-1.5 text-left", className),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx11(
  "div",
  {
    className: cn(
      // Mobile: sticky footer pinned above the safe-area inset so primary
      // actions stay reachable while content scrolls and the keyboard is up.
      "sticky bottom-0 -mx-5 -mb-5 mt-2 flex flex-col-reverse gap-2 border-t bg-background px-5 py-3 pb-safe-or-3",
      "sm:static sm:m-0 sm:border-t-0 sm:bg-transparent sm:p-0 sm:pb-0 sm:flex-row sm:justify-end",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx11(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// src/components/empty-state.tsx
import { jsx as jsx12, jsxs as jsxs8 } from "react/jsx-runtime";
function EmptyState({
  icon,
  title,
  description,
  action,
  className
}) {
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      className: cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-card px-6 py-12 text-center",
        className
      ),
      children: [
        icon && /* @__PURE__ */ jsx12("div", { className: "flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground", children: icon }),
        /* @__PURE__ */ jsxs8("div", { className: "grid gap-1", children: [
          /* @__PURE__ */ jsx12("h3", { className: "text-base font-semibold", children: title }),
          description && /* @__PURE__ */ jsx12("p", { className: "mx-auto max-w-md text-sm text-muted-foreground", children: description })
        ] }),
        action && /* @__PURE__ */ jsx12("div", { className: "mt-2", children: action })
      ]
    }
  );
}

// src/components/form-field.tsx
import * as React8 from "react";

// src/components/label.tsx
import * as React7 from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx13 } from "react/jsx-runtime";
var labelVariants = cva3(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx13(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants(), className),
    ...props
  }
));
Label.displayName = LabelPrimitive.Root.displayName;

// src/components/form-field.tsx
import { jsx as jsx14, jsxs as jsxs9 } from "react/jsx-runtime";
var idCounter = 0;
function uid() {
  idCounter += 1;
  return `lk-field-${idCounter}`;
}
function FormField({
  id,
  label,
  description,
  error,
  required,
  optional,
  children,
  className
}) {
  const reactId = React8.useId?.() ?? "";
  const fieldId = id ?? children.props.id ?? `${reactId || uid()}-input`;
  const errorId = `${fieldId}-error`;
  const descriptionId = `${fieldId}-description`;
  const child = React8.cloneElement(children, {
    id: fieldId,
    "aria-invalid": error ? true : children.props["aria-invalid"],
    "aria-describedby": (
      // The description <p> is not rendered while an error is shown, so
      // only reference it when it actually exists in the DOM.
      [error ? errorId : null, description && !error ? descriptionId : null].filter(Boolean).join(" ") || void 0
    )
  });
  return /* @__PURE__ */ jsxs9("div", { className: cn("grid gap-1.5", className), children: [
    label && /* @__PURE__ */ jsxs9(Label, { htmlFor: fieldId, className: "flex items-center gap-1", children: [
      label,
      required && /* @__PURE__ */ jsx14("span", { className: "text-destructive", children: "*" }),
      optional && /* @__PURE__ */ jsx14("span", { className: "text-xs font-normal text-muted-foreground", children: "(optional)" })
    ] }),
    child,
    description && !error && /* @__PURE__ */ jsx14("p", { id: descriptionId, className: "text-xs text-muted-foreground", children: description }),
    error && /* @__PURE__ */ jsx14("p", { id: errorId, className: "text-xs text-destructive", children: error })
  ] });
}

// src/components/input.tsx
import * as React9 from "react";
import { jsx as jsx15 } from "react/jsx-runtime";
var Input = React9.forwardRef(
  ({ className, type, ...props }, ref) => {
    return /* @__PURE__ */ jsx15(
      "input",
      {
        type,
        className: cn(
          // Touch-first height; the global @media rule in styles.css bumps the
          // font-size to 16px below sm so iOS doesn't zoom on focus.
          "flex h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/30 sm:h-9",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/components/inventory-search/build-inventory-search-request.ts
var FACET_SECTIONS = [
  { kind: "term", key: "make", label: "Make" },
  { kind: "term", key: "model", label: "Model" },
  { kind: "term", key: "trim", label: "Trim" },
  { kind: "range", key: "year", label: "Year" },
  { kind: "term", key: "body", label: "Body" },
  { kind: "range", key: "mileage", label: "Mileage" },
  { kind: "range", key: "price", label: "Price" },
  { kind: "term", key: "condition", label: "Condition" },
  { kind: "term", key: "status", label: "Status" },
  { kind: "term", key: "fuel", label: "Fuel" },
  { kind: "term", key: "transmission", label: "Transmission" },
  { kind: "term", key: "driveTrain", label: "Drivetrain" },
  { kind: "term", key: "color", label: "Color" },
  { kind: "term", key: "interiorColor", label: "Interior" },
  { kind: "rooftop", label: "Rooftop" }
];
var SORT_OPTIONS = [
  { value: "stockDate.desc", field: "stockDate", order: "desc", label: "Newest in stock" },
  { value: "stockDate.asc", field: "stockDate", order: "asc", label: "Oldest in stock" },
  { value: "price.asc", field: "price", order: "asc", label: "Price \u2191" },
  { value: "price.desc", field: "price", order: "desc", label: "Price \u2193" },
  { value: "mileage.asc", field: "mileage", order: "asc", label: "Mileage \u2191" },
  { value: "year.desc", field: "year", order: "desc", label: "Year \u2193" }
];
function buildInventorySearchRequest(params, pageSize, options = {}) {
  const filters = {};
  for (const section of FACET_SECTIONS) {
    if (section.kind !== "term") continue;
    const raw = params.get(section.key);
    if (raw) {
      filters[section.key] = raw.split(",").filter(Boolean);
    }
  }
  const rooftopRaw = params.get("rooftopId");
  if (rooftopRaw) {
    filters.rooftopId = rooftopRaw.split(",").filter(Boolean);
  }
  if (options.includeImportId) {
    const importRaw = params.get("importId");
    if (importRaw) {
      filters.importId = importRaw.split(",").filter(Boolean);
    }
  }
  for (const section of FACET_SECTIONS) {
    if (section.kind !== "range") continue;
    const from = params.get(`${section.key}.from`);
    const to = params.get(`${section.key}.to`);
    if (from != null || to != null) {
      filters[section.key] = {
        from: from != null ? Number(from) : void 0,
        to: to != null ? Number(to) : void 0
      };
    }
  }
  const sort = params.get("sort");
  const validSort = SORT_OPTIONS.find((o) => o.value === sort);
  const page = Math.max(1, Number(params.get("page")) || 1);
  const query = params.get("q")?.trim() || void 0;
  return {
    query,
    filters,
    sort: validSort ? { field: validSort.field, order: validSort.order } : { field: "stockDate", order: "desc" },
    pagination: { skip: (page - 1) * pageSize, limit: pageSize },
    facets: true
  };
}

// src/components/inventory-search/inventory-search-toolbar.tsx
import * as React11 from "react";
import {
  ChevronDown as ChevronDown2,
  Search,
  SlidersHorizontal,
  X as X2
} from "lucide-react";
import { INVENTORY_STATUS_LABELS } from "@lumika/shared";

// src/components/select.tsx
import * as React10 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs10(
  SelectPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex h-11 w-full min-w-0 items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/30 [&>span]:line-clamp-1 sm:h-9",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx16(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx16(ChevronDown, { className: "h-4 w-4 opacity-50" }) })
    ]
  }
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn("flex cursor-default items-center justify-center py-1", className),
    ...props,
    children: /* @__PURE__ */ jsx16(ChevronUp, { className: "h-4 w-4" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn("flex cursor-default items-center justify-center py-1", className),
    ...props,
    children: /* @__PURE__ */ jsx16(ChevronDown, { className: "h-4 w-4" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React10.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx16(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs10(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: [
      /* @__PURE__ */ jsx16(SelectScrollUpButton, {}),
      /* @__PURE__ */ jsx16(
        SelectPrimitive.Viewport,
        {
          className: cn(
            "p-1",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          children
        }
      ),
      /* @__PURE__ */ jsx16(SelectScrollDownButton, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  SelectPrimitive.Label,
  {
    ref,
    className: cn("px-2 py-1.5 text-sm font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs10(
  SelectPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 sm:py-1.5",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx16("span", { className: "absolute right-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx16(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx16(Check, { className: "h-4 w-4" }) }) }),
      /* @__PURE__ */ jsx16(SelectPrimitive.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// src/components/inventory-search/inventory-search-toolbar.tsx
import { Fragment, jsx as jsx17, jsxs as jsxs11 } from "react/jsx-runtime";
function InventorySearchToolbar({
  params,
  onParamsChange,
  facets,
  loading = false,
  includeImportFilter = false,
  searchPlaceholder = "Search VIN, stock, make, model\u2026",
  currency = "USD"
}) {
  const [searchInput, setSearchInput] = React11.useState(params.get("q") ?? "");
  const urlQuery = params.get("q") ?? "";
  React11.useEffect(() => {
    setSearchInput((prev) => prev === urlQuery ? prev : urlQuery);
  }, [urlQuery]);
  const paramsRef = React11.useRef(params);
  const onParamsChangeRef = React11.useRef(onParamsChange);
  React11.useEffect(() => {
    paramsRef.current = params;
  }, [params]);
  React11.useEffect(() => {
    onParamsChangeRef.current = onParamsChange;
  }, [onParamsChange]);
  React11.useEffect(() => {
    const t = window.setTimeout(() => {
      const current = paramsRef.current;
      const next = new URLSearchParams(current);
      if (searchInput.trim()) next.set("q", searchInput.trim());
      else next.delete("q");
      next.delete("page");
      if (next.toString() !== current.toString()) {
        onParamsChangeRef.current(next, { replace: true });
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);
  const filters = React11.useMemo(
    () => buildInventorySearchRequest(params, 1, {
      includeImportId: includeImportFilter
    }).filters ?? {},
    [params, includeImportFilter]
  );
  const sort = params.get("sort") ?? "stockDate.desc";
  const setTermFilter = (key, values) => {
    const next = new URLSearchParams(params);
    if (values.length) next.set(key, values.join(","));
    else next.delete(key);
    next.delete("page");
    onParamsChange(next, { replace: true });
  };
  const setRangeFilter = (key, range) => {
    const next = new URLSearchParams(params);
    if (range.from != null) next.set(`${key}.from`, String(range.from));
    else next.delete(`${key}.from`);
    if (range.to != null) next.set(`${key}.to`, String(range.to));
    else next.delete(`${key}.to`);
    next.delete("page");
    onParamsChange(next, { replace: true });
  };
  const setSort = (value) => {
    const next = new URLSearchParams(params);
    next.set("sort", value);
    next.delete("page");
    onParamsChange(next, { replace: true });
  };
  const clearAll = () => {
    const next = new URLSearchParams();
    const q = params.get("q");
    const s = params.get("sort");
    if (q) next.set("q", q);
    if (s) next.set("sort", s);
    onParamsChange(next, { replace: true });
  };
  const activeChips = React11.useMemo(
    () => collectActiveChips(filters, facets, includeImportFilter, currency),
    [filters, facets, includeImportFilter, currency]
  );
  return /* @__PURE__ */ jsxs11("div", { className: "sticky top-0 z-20 -mx-4 border-b bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:-mx-6 md:px-6", children: [
    /* @__PURE__ */ jsxs11("div", { className: "relative flex flex-wrap items-center gap-2", children: [
      /* @__PURE__ */ jsxs11("div", { className: "relative min-w-[16rem] flex-1", children: [
        /* @__PURE__ */ jsx17(Search, { className: "pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" }),
        /* @__PURE__ */ jsx17(
          Input,
          {
            value: searchInput,
            onChange: (e) => setSearchInput(e.target.value),
            placeholder: searchPlaceholder,
            className: "pl-9",
            "aria-label": "Search inventory"
          }
        )
      ] }),
      /* @__PURE__ */ jsx17(
        FiltersPopover,
        {
          facets,
          filters,
          activeCount: activeChips.length,
          loading: loading && !facets,
          onChangeTerm: setTermFilter,
          onChangeRange: setRangeFilter
        }
      ),
      /* @__PURE__ */ jsxs11(Select, { value: sort, onValueChange: setSort, children: [
        /* @__PURE__ */ jsx17(SelectTrigger, { className: "w-44", "aria-label": "Sort order", children: /* @__PURE__ */ jsx17(SelectValue, {}) }),
        /* @__PURE__ */ jsx17(SelectContent, { children: SORT_OPTIONS.map((o) => /* @__PURE__ */ jsx17(SelectItem, { value: o.value, children: o.label }, o.value)) })
      ] }),
      activeChips.length > 0 && /* @__PURE__ */ jsx17(Button, { type: "button", variant: "ghost", size: "sm", onClick: clearAll, children: "Clear all" })
    ] }),
    activeChips.length > 0 && /* @__PURE__ */ jsx17("div", { className: "mt-2 flex flex-wrap items-center gap-1.5", children: activeChips.map((chip) => /* @__PURE__ */ jsx17(
      FilterChip,
      {
        label: chip.label,
        onRemove: () => chip.remove({
          setTermFilter,
          setRangeFilter
        })
      },
      chip.id
    )) })
  ] });
}
function collectActiveChips(filters, facets, includeImportFilter, currency) {
  const chips = [];
  for (const section of FACET_SECTIONS) {
    if (section.kind === "term") {
      const selected = filters[section.key] ?? [];
      for (const value of selected) {
        chips.push({
          id: `${section.key}:${value}`,
          label: `${section.label}: ${humanizeTermValue(section.key, value)}`,
          remove: ({ setTermFilter }) => setTermFilter(
            section.key,
            selected.filter((v) => v !== value)
          )
        });
      }
    } else if (section.kind === "range") {
      const range = filters[section.key];
      if (range && (range.from != null || range.to != null)) {
        chips.push({
          id: `${section.key}:range`,
          label: `${section.label}: ${formatRangeLabel(section.key, range, currency)}`,
          remove: ({ setRangeFilter }) => setRangeFilter(section.key, { from: null, to: null })
        });
      }
    } else if (section.kind === "rooftop") {
      const selected = filters.rooftopId ?? [];
      for (const id of selected) {
        const label = facets?.rooftop.find((r) => r.value === id)?.label ?? id;
        chips.push({
          id: `rooftop:${id}`,
          label: `Rooftop: ${label}`,
          remove: ({ setTermFilter }) => setTermFilter(
            "rooftopId",
            selected.filter((v) => v !== id)
          )
        });
      }
    }
  }
  if (includeImportFilter) {
    const selected = filters.importId ?? [];
    for (const id of selected) {
      chips.push({
        id: `importId:${id}`,
        label: `Import: ${id.slice(0, 8)}\u2026`,
        remove: ({ setTermFilter }) => setTermFilter(
          "importId",
          selected.filter((v) => v !== id)
        )
      });
    }
  }
  return chips;
}
function humanizeTermValue(key, value) {
  if (key === "status") {
    return INVENTORY_STATUS_LABELS[value] ?? value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
}
function formatRangeLabel(key, range, currency) {
  const fmt = (n) => {
    if (key === "price") {
      return new Intl.NumberFormat(currency === "CAD" ? "en-CA" : "en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: 0
      }).format(n);
    }
    if (key === "mileage") return `${n.toLocaleString()} mi`;
    return n.toLocaleString();
  };
  if (range.from != null && range.to != null) {
    return `${fmt(range.from)} \u2013 ${fmt(range.to)}`;
  }
  if (range.from != null) return `\u2265 ${fmt(range.from)}`;
  if (range.to != null) return `\u2264 ${fmt(range.to)}`;
  return "";
}
function FilterChip({
  label,
  onRemove
}) {
  return /* @__PURE__ */ jsxs11(
    "button",
    {
      type: "button",
      onClick: onRemove,
      className: "group inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/15",
      "aria-label": `Remove filter: ${label}`,
      children: [
        /* @__PURE__ */ jsx17("span", { children: label }),
        /* @__PURE__ */ jsx17(X2, { className: "size-3 opacity-60 transition-opacity group-hover:opacity-100" })
      ]
    }
  );
}
function FiltersPopover({
  facets,
  filters,
  activeCount,
  loading,
  onChangeTerm,
  onChangeRange
}) {
  const [open, setOpen] = React11.useState(false);
  const ref = React11.useRef(null);
  const triggerRef = React11.useRef(null);
  const panelRef = React11.useRef(null);
  React11.useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    function onDown(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    // Below sm the wrapper is `static` so the panel anchors to the
    // (relative) toolbar row instead of the button — right-0 then sits
    // flush with the row's right edge and stays fully on-screen even
    // when flex-wrap pushes the Filters button to a new row.
    /* @__PURE__ */ jsxs11("div", { ref, className: "static sm:relative", children: [
      /* @__PURE__ */ jsxs11(
        Button,
        {
          ref: triggerRef,
          type: "button",
          variant: "outline",
          size: "sm",
          onClick: () => setOpen((o) => !o),
          "aria-expanded": open,
          "aria-haspopup": "dialog",
          className: cn(
            activeCount > 0 && "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"
          ),
          children: [
            /* @__PURE__ */ jsx17(SlidersHorizontal, { className: "size-4" }),
            "Filters",
            activeCount > 0 && /* @__PURE__ */ jsx17("span", { className: "rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-foreground", children: activeCount })
          ]
        }
      ),
      open && /* @__PURE__ */ jsx17(
        "div",
        {
          ref: panelRef,
          role: "dialog",
          "aria-label": "Inventory filters",
          tabIndex: -1,
          className: "absolute right-0 top-full z-40 mt-1.5 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border bg-card shadow-lg outline-none",
          children: /* @__PURE__ */ jsx17("div", { className: "max-h-[70vh] overflow-y-auto", children: loading && !facets ? /* @__PURE__ */ jsx17("p", { className: "px-3 py-4 text-sm text-muted-foreground", children: "Loading filters\u2026" }) : !facets ? /* @__PURE__ */ jsx17("p", { className: "px-3 py-4 text-sm text-muted-foreground", children: "Filters will appear once inventory loads." }) : FACET_SECTIONS.map((section) => {
            if (section.kind === "term") {
              const buckets = facets[section.key];
              if (!buckets || buckets.length === 0) return null;
              const selected2 = filters[section.key] ?? [];
              return /* @__PURE__ */ jsx17(
                FilterSection,
                {
                  label: section.label,
                  count: selected2.length,
                  children: /* @__PURE__ */ jsx17(
                    CheckboxList,
                    {
                      label: section.label,
                      buckets: buckets.map((b) => ({
                        ...b,
                        // Same humanization the chips use, so e.g.
                        // status reads "Available" in both places.
                        label: humanizeTermValue(section.key, b.value)
                      })),
                      selected: selected2,
                      onChange: (next) => onChangeTerm(section.key, next)
                    }
                  )
                },
                section.key
              );
            }
            if (section.kind === "range") {
              const r = facets[section.key];
              if (!r || r.from == null || r.to == null) return null;
              const active = filters[section.key];
              const hasActive = active?.from != null || active?.to != null;
              return /* @__PURE__ */ jsx17(
                FilterSection,
                {
                  label: section.label,
                  count: hasActive ? 1 : 0,
                  children: /* @__PURE__ */ jsx17(
                    RangeInputs,
                    {
                      unit: section.key,
                      min: r.from,
                      max: r.to,
                      value: {
                        from: active?.from ?? null,
                        to: active?.to ?? null
                      },
                      onChange: (next) => onChangeRange(section.key, next)
                    }
                  )
                },
                section.key
              );
            }
            if (!facets.rooftop || facets.rooftop.length <= 1) return null;
            const selected = filters.rooftopId ?? [];
            return /* @__PURE__ */ jsx17(
              FilterSection,
              {
                label: section.label,
                count: selected.length,
                children: /* @__PURE__ */ jsx17(
                  CheckboxList,
                  {
                    label: section.label,
                    buckets: facets.rooftop.map((r) => ({
                      value: r.value,
                      count: r.count,
                      label: r.label
                    })),
                    selected,
                    onChange: (next) => onChangeTerm("rooftopId", next)
                  }
                )
              },
              "rooftop"
            );
          }) })
        }
      )
    ] })
  );
}
function FilterSection({
  label,
  count,
  children
}) {
  const [open, setOpen] = React11.useState(count > 0);
  return /* @__PURE__ */ jsxs11("div", { className: "border-b border-border last:border-b-0", children: [
    /* @__PURE__ */ jsxs11(
      "button",
      {
        type: "button",
        onClick: () => setOpen((o) => !o),
        "aria-expanded": open,
        className: "flex w-full items-center justify-between gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted/50",
        children: [
          /* @__PURE__ */ jsxs11("span", { className: "flex items-center gap-1.5 font-medium", children: [
            label,
            count > 0 && /* @__PURE__ */ jsx17("span", { className: "rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-foreground", children: count })
          ] }),
          /* @__PURE__ */ jsx17(
            ChevronDown2,
            {
              className: cn(
                "size-3.5 shrink-0 text-muted-foreground transition-transform",
                open && "rotate-180"
              )
            }
          )
        ]
      }
    ),
    open && /* @__PURE__ */ jsx17("div", { className: "px-2 pb-2", children })
  ] });
}
function CheckboxList({
  label,
  buckets,
  selected,
  onChange
}) {
  const [search, setSearch] = React11.useState("");
  const needle = search.trim().toLowerCase();
  const filtered = needle ? buckets.filter(
    (b) => (b.label ?? b.value).toLowerCase().includes(needle)
  ) : buckets;
  const selectedSet = new Set(selected);
  const pinned = filtered.filter((b) => selectedSet.has(b.value));
  const rest = filtered.filter((b) => !selectedSet.has(b.value));
  const ordered = [...pinned, ...rest];
  const toggle = (value) => {
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
    );
  };
  return /* @__PURE__ */ jsxs11(Fragment, { children: [
    buckets.length > 7 && /* @__PURE__ */ jsx17(
      Input,
      {
        value: search,
        onChange: (e) => setSearch(e.target.value),
        placeholder: `Search ${label.toLowerCase()}\u2026`,
        className: "mb-1.5 h-8",
        "aria-label": `Search ${label}`
      }
    ),
    /* @__PURE__ */ jsx17("div", { className: "max-h-52 overflow-y-auto", children: ordered.length === 0 ? /* @__PURE__ */ jsx17("p", { className: "px-2 py-3 text-center text-xs text-muted-foreground", children: "No matches" }) : ordered.map((b) => /* @__PURE__ */ jsxs11(
      "label",
      {
        className: "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/60",
        children: [
          /* @__PURE__ */ jsx17(
            "input",
            {
              type: "checkbox",
              checked: selectedSet.has(b.value),
              onChange: () => toggle(b.value),
              className: "size-4 shrink-0 accent-primary"
            }
          ),
          /* @__PURE__ */ jsx17("span", { className: "flex-1 truncate", title: b.label ?? b.value, children: b.label ?? b.value }),
          /* @__PURE__ */ jsx17("span", { className: "text-xs tabular-nums text-muted-foreground", children: b.count.toLocaleString() })
        ]
      },
      b.value
    )) })
  ] });
}
function RangeInputs({
  unit,
  min,
  max,
  value,
  onChange
}) {
  const [fromText, setFromText] = React11.useState(value.from?.toString() ?? "");
  const [toText, setToText] = React11.useState(value.to?.toString() ?? "");
  React11.useEffect(() => {
    setFromText(value.from?.toString() ?? "");
  }, [value.from]);
  React11.useEffect(() => {
    setToText(value.to?.toString() ?? "");
  }, [value.to]);
  const parse = (t) => {
    const cleaned = t.replace(/[$,\s]/g, "");
    if (cleaned === "") return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };
  const commit = () => {
    onChange({ from: parse(fromText), to: parse(toText) });
  };
  const placeholder = (n) => {
    if (unit === "price") return `$${n.toLocaleString()}`;
    return n.toLocaleString();
  };
  return /* @__PURE__ */ jsxs11("div", { className: "grid gap-2 p-1", children: [
    /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2 text-sm", children: [
      /* @__PURE__ */ jsx17(
        Input,
        {
          inputMode: "numeric",
          placeholder: placeholder(min),
          value: fromText,
          onChange: (e) => setFromText(e.target.value),
          onBlur: commit,
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          },
          className: "h-8",
          "aria-label": "From"
        }
      ),
      /* @__PURE__ */ jsx17("span", { className: "text-muted-foreground", children: "\u2013" }),
      /* @__PURE__ */ jsx17(
        Input,
        {
          inputMode: "numeric",
          placeholder: placeholder(max),
          value: toText,
          onChange: (e) => setToText(e.target.value),
          onBlur: commit,
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          },
          className: "h-8",
          "aria-label": "To"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs11("p", { className: "text-xs text-muted-foreground", children: [
      "Available range: ",
      placeholder(min),
      " \u2013 ",
      placeholder(max)
    ] })
  ] });
}

// src/components/search-input.tsx
import * as React12 from "react";
import { Search as Search2 } from "lucide-react";
import { jsx as jsx18, jsxs as jsxs12 } from "react/jsx-runtime";
var SearchInput = React12.forwardRef(
  ({ className, containerClassName, ...props }, ref) => {
    return /* @__PURE__ */ jsxs12("div", { className: cn("relative", containerClassName), children: [
      /* @__PURE__ */ jsx18(Search2, { className: "pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" }),
      /* @__PURE__ */ jsx18(Input, { ref, className: cn("pl-8", className), ...props })
    ] });
  }
);
SearchInput.displayName = "SearchInput";

// src/components/mobile-app-bar.tsx
import { jsx as jsx19, jsxs as jsxs13 } from "react/jsx-runtime";
function MobileAppBar({
  leading,
  title,
  subtitle,
  trailing,
  borderless = false,
  className
}) {
  return /* @__PURE__ */ jsx19(
    "header",
    {
      className: cn(
        "sticky top-0 z-30 flex items-center gap-2 bg-background pt-safe md:hidden",
        borderless ? "" : "border-b",
        className
      ),
      children: /* @__PURE__ */ jsxs13("div", { className: "flex h-app-bar w-full items-center gap-2 px-2", children: [
        /* @__PURE__ */ jsx19("div", { className: "flex min-w-touch items-center", children: leading }),
        /* @__PURE__ */ jsxs13("div", { className: "min-w-0 flex-1 text-center", children: [
          title && /* @__PURE__ */ jsx19("div", { className: "truncate text-sm font-semibold tracking-tight", children: title }),
          subtitle && /* @__PURE__ */ jsx19("div", { className: "truncate text-[11px] text-muted-foreground", children: subtitle })
        ] }),
        /* @__PURE__ */ jsx19("div", { className: "flex min-w-touch items-center justify-end gap-0.5", children: trailing })
      ] })
    }
  );
}

// src/components/mobile-list.tsx
import { ChevronRight as ChevronRight2 } from "lucide-react";
import { jsx as jsx20, jsxs as jsxs14 } from "react/jsx-runtime";
function MobileList({
  className,
  children
}) {
  return /* @__PURE__ */ jsx20("ul", { className: cn("divide-y rounded-lg border bg-card md:hidden", className), children });
}
function MobileListRow(props) {
  const { primary, secondary, tertiary, trailing, meta, className, onClick, href } = props;
  const interactive = typeof onClick === "function";
  const text = /* @__PURE__ */ jsxs14("div", { className: "min-w-0 flex-1", children: [
    /* @__PURE__ */ jsx20("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsx20("span", { className: "min-w-0 truncate text-sm font-medium text-foreground", children: primary }) }),
    secondary && /* @__PURE__ */ jsx20("div", { className: "mt-0.5 truncate text-xs text-muted-foreground", children: secondary }),
    tertiary && /* @__PURE__ */ jsx20("div", { className: "mt-0.5 truncate text-xs text-muted-foreground", children: tertiary }),
    meta && /* @__PURE__ */ jsx20("div", { className: "mt-1.5 flex flex-wrap items-center gap-1.5", children: meta })
  ] });
  const tapClasses = cn(
    "flex min-h-touch min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60 active:bg-muted",
    className
  );
  const chevron = /* @__PURE__ */ jsx20(ChevronRight2, { "aria-hidden": true, className: "mt-0.5 h-4 w-4 shrink-0 self-center text-muted-foreground" });
  const trailingSibling = trailing ? /* @__PURE__ */ jsx20("div", { className: "flex shrink-0 items-center gap-1 self-center pr-4", children: trailing }) : null;
  if (interactive || href) {
    const tap = interactive ? /* @__PURE__ */ jsxs14("button", { type: "button", onClick, className: tapClasses, children: [
      text,
      !trailing && chevron
    ] }) : /* @__PURE__ */ jsxs14("a", { href, className: tapClasses, children: [
      text,
      !trailing && chevron
    ] });
    return /* @__PURE__ */ jsxs14("li", { className: "flex items-stretch", children: [
      tap,
      trailingSibling
    ] });
  }
  return /* @__PURE__ */ jsx20("li", { className: cn("min-h-touch", className), children: /* @__PURE__ */ jsxs14("div", { className: "flex min-w-0 items-start gap-3 px-4 py-3", children: [
    text,
    trailing && /* @__PURE__ */ jsx20("div", { className: "flex shrink-0 flex-col items-end gap-1 pt-0.5", children: trailing })
  ] }) });
}

// src/components/page-header.tsx
import { jsx as jsx21, jsxs as jsxs15 } from "react/jsx-runtime";
function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className
}) {
  return /* @__PURE__ */ jsxs15(
    "header",
    {
      className: cn(
        // Tighter gaps and smaller title on phones; full desktop scale at md+.
        "flex flex-col gap-3 border-b pb-4 md:gap-4 md:pb-5 md:flex-row md:items-start md:justify-between",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs15("div", { className: "min-w-0", children: [
          eyebrow && /* @__PURE__ */ jsx21("p", { className: "text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground", children: eyebrow }),
          /* @__PURE__ */ jsx21("h1", { className: "mt-1 text-xl font-semibold tracking-tight leading-tight truncate md:text-2xl", children: title }),
          description && /* @__PURE__ */ jsx21("p", { className: "mt-2 max-w-2xl text-sm text-muted-foreground", children: description })
        ] }),
        actions && // On mobile the primary action stretches edge-to-edge so it's easy to
        // hit; at md+ it floats right next to the title.
        /* @__PURE__ */ jsx21("div", { className: "flex min-w-0 flex-wrap items-stretch gap-2 [&>*]:flex-1 md:items-center md:[&>*]:flex-initial", children: actions })
      ]
    }
  );
}

// src/components/page-state.tsx
import { AlertTriangle, Loader2 } from "lucide-react";
import { jsx as jsx22, jsxs as jsxs16 } from "react/jsx-runtime";
function PageLoading({
  message = "Loading\u2026",
  className
}) {
  return /* @__PURE__ */ jsxs16(
    "div",
    {
      className: cn(
        "flex min-h-[40vh] flex-col items-center justify-center gap-3 text-sm text-muted-foreground",
        className
      ),
      role: "status",
      "aria-live": "polite",
      children: [
        /* @__PURE__ */ jsx22(Loader2, { className: "h-4 w-4 animate-spin" }),
        /* @__PURE__ */ jsx22("span", { children: message })
      ]
    }
  );
}
function PageError({
  title = "Something went wrong",
  description,
  action,
  className
}) {
  return /* @__PURE__ */ jsxs16(
    "div",
    {
      className: cn(
        "flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center",
        className
      ),
      role: "alert",
      children: [
        /* @__PURE__ */ jsx22("div", { className: "flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive", children: /* @__PURE__ */ jsx22(AlertTriangle, { className: "h-5 w-5" }) }),
        /* @__PURE__ */ jsxs16("div", { className: "grid gap-1", children: [
          /* @__PURE__ */ jsx22("h3", { className: "text-base font-semibold", children: title }),
          description && /* @__PURE__ */ jsx22("p", { className: "mx-auto max-w-md text-sm text-muted-foreground", children: description })
        ] }),
        action && /* @__PURE__ */ jsx22("div", { className: "mt-2", children: action })
      ]
    }
  );
}

// src/components/phone-input.tsx
import * as React13 from "react";
import { jsx as jsx23 } from "react/jsx-runtime";
var MAX_NATIONAL = 10;
function nationalDigits(raw) {
  let d = raw.replace(/\D/g, "");
  if (d.startsWith("1")) d = d.slice(1);
  return d.slice(0, MAX_NATIONAL);
}
function formatPhoneDisplay(raw) {
  const national = nationalDigits(raw ?? "");
  if (!national) return "";
  const area = national.slice(0, 3);
  const prefix = national.slice(3, 6);
  const line = national.slice(6, 10);
  let out = `+1 (${area}`;
  if (area.length === 3) out += ")";
  if (prefix) out += ` ${prefix}`;
  if (line) out += `-${line}`;
  return out;
}
function phoneInputToE164(raw) {
  const national = nationalDigits(raw ?? "");
  return national ? `+1${national}` : "";
}
function nextPhoneValue(current, nextRaw) {
  const currentDigits = nationalDigits(current ?? "");
  const nextDigits = nationalDigits(nextRaw);
  const deletedOnlyFormatting = nextDigits === currentDigits && nextRaw.length < formatPhoneDisplay(current ?? "").length;
  if (deletedOnlyFormatting) {
    return phoneInputToE164(currentDigits.slice(0, -1));
  }
  return phoneInputToE164(nextRaw);
}
var PhoneInput = React13.forwardRef(
  ({ value, onChange, placeholder, ...props }, ref) => {
    return /* @__PURE__ */ jsx23(
      Input,
      {
        ...props,
        ref,
        type: "tel",
        inputMode: "tel",
        autoComplete: "tel",
        value: formatPhoneDisplay(value ?? ""),
        placeholder: placeholder ?? "+1 (555) 123-4567",
        onChange: (e) => onChange(nextPhoneValue(value ?? "", e.target.value))
      }
    );
  }
);
PhoneInput.displayName = "PhoneInput";

// src/components/section.tsx
import { jsx as jsx24, jsxs as jsxs17 } from "react/jsx-runtime";
function Section({
  title,
  description,
  actions,
  children,
  className,
  contentClassName
}) {
  return /* @__PURE__ */ jsxs17(
    "section",
    {
      className: cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm",
        className
      ),
      children: [
        (title || description || actions) && /* @__PURE__ */ jsxs17("header", { className: "flex flex-col gap-3 border-b px-6 py-4 md:flex-row md:items-start md:justify-between md:gap-4", children: [
          /* @__PURE__ */ jsxs17("div", { className: "min-w-0", children: [
            title && /* @__PURE__ */ jsx24("h2", { className: "text-base font-semibold leading-none tracking-tight", children: title }),
            description && /* @__PURE__ */ jsx24("p", { className: "mt-1.5 text-sm text-muted-foreground", children: description })
          ] }),
          actions && /* @__PURE__ */ jsx24("div", { className: "flex flex-wrap items-center gap-2 md:shrink-0", children: actions })
        ] }),
        /* @__PURE__ */ jsx24("div", { className: cn("p-6", contentClassName), children })
      ]
    }
  );
}

// src/components/separator.tsx
import * as React14 from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { jsx as jsx25 } from "react/jsx-runtime";
var Separator2 = React14.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx25(
    SeparatorPrimitive.Root,
    {
      ref,
      decorative,
      orientation,
      className: cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator2.displayName = SeparatorPrimitive.Root.displayName;

// src/components/sheet.tsx
import * as React15 from "react";
import * as DialogPrimitive2 from "@radix-ui/react-dialog";
import { X as X3 } from "lucide-react";
import { cva as cva4 } from "class-variance-authority";
import { jsx as jsx26, jsxs as jsxs18 } from "react/jsx-runtime";
var Sheet = DialogPrimitive2.Root;
var SheetTrigger = DialogPrimitive2.Trigger;
var SheetClose = DialogPrimitive2.Close;
var SheetPortal = DialogPrimitive2.Portal;
var SheetOverlay = React15.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx26(
  DialogPrimitive2.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
SheetOverlay.displayName = "SheetOverlay";
var sheetVariants = cva4(
  "fixed z-50 flex flex-col gap-3 border bg-background shadow-xl outline-none transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        // The numeric suffix on slide-in-from/out-to is tailwindcss-animate's
        // distance multiplier — 96 ≈ 24rem, big enough to slide a sheet's
        // entire footprint without compositing artifacts.
        bottom: "inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl border-x-0 border-b-0 pb-safe data-[state=open]:slide-in-from-bottom-96 data-[state=closed]:slide-out-to-bottom-96",
        top: "inset-x-0 top-0 max-h-[92dvh] rounded-b-2xl border-x-0 border-t-0 pt-safe data-[state=open]:slide-in-from-top-96 data-[state=closed]:slide-out-to-top-96",
        left: "inset-y-0 left-0 w-[85vw] max-w-sm border-l-0 pb-safe pt-safe data-[state=open]:slide-in-from-left-96 data-[state=closed]:slide-out-to-left-96",
        right: "inset-y-0 right-0 w-[85vw] max-w-sm border-r-0 pb-safe pt-safe data-[state=open]:slide-in-from-right-96 data-[state=closed]:slide-out-to-right-96"
      }
    },
    defaultVariants: { side: "bottom" }
  }
);
var SheetContent = React15.forwardRef(({ className, children, side = "bottom", hideClose = false, ...props }, ref) => /* @__PURE__ */ jsxs18(SheetPortal, { children: [
  /* @__PURE__ */ jsx26(SheetOverlay, {}),
  /* @__PURE__ */ jsxs18(
    DialogPrimitive2.Content,
    {
      ref,
      className: cn(sheetVariants({ side }), className),
      ...props,
      children: [
        children,
        !hideClose && /* @__PURE__ */ jsx26(
          DialogPrimitive2.Close,
          {
            "aria-label": "Close",
            className: "absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring",
            children: /* @__PURE__ */ jsx26(X3, { className: "h-4 w-4" })
          }
        )
      ]
    }
  )
] }));
SheetContent.displayName = "SheetContent";
var SheetHeader = ({ className, ...props }) => /* @__PURE__ */ jsx26("div", { className: cn("flex flex-col gap-1 px-5 pt-2 text-left", className), ...props });
SheetHeader.displayName = "SheetHeader";
var SheetFooter = ({ className, ...props }) => /* @__PURE__ */ jsx26(
  "div",
  {
    className: cn(
      "mt-auto flex flex-col-reverse gap-2 border-t bg-background px-5 py-3 sm:flex-row sm:justify-end",
      className
    ),
    ...props
  }
);
SheetFooter.displayName = "SheetFooter";
var SheetTitle = React15.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx26(
  DialogPrimitive2.Title,
  {
    ref,
    className: cn("text-base font-semibold tracking-tight", className),
    ...props
  }
));
SheetTitle.displayName = "SheetTitle";
var SheetDescription = React15.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx26(
  DialogPrimitive2.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
SheetDescription.displayName = "SheetDescription";
var SheetBody = ({ className, ...props }) => (
  // min-h-0 lets this flex child shrink to the sheet's max-height so
  // long content scrolls inside it instead of running past the viewport.
  /* @__PURE__ */ jsx26("div", { className: cn("min-h-0 flex-1 overflow-y-auto px-5 py-3", className), ...props })
);
SheetBody.displayName = "SheetBody";

// src/components/sonner.tsx
import { Toaster as Sonner } from "sonner";
import { toast } from "sonner";
import { jsx as jsx27 } from "react/jsx-runtime";
var Toaster = ({ ...props }) => {
  return /* @__PURE__ */ jsx27(
    Sonner,
    {
      theme: "light",
      className: "toaster group",
      toastOptions: {
        classNames: {
          toast: "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground"
        }
      },
      ...props
    }
  );
};

// src/components/switch.tsx
import * as React16 from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { jsx as jsx28 } from "react/jsx-runtime";
var Switch = React16.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx28(
  SwitchPrimitives.Root,
  {
    className: cn(
      // The visible rail is 20×36px, but a transparent `::before` overlay
      // expands the hit area to the 44px touch minimum without changing
      // the visual size.
      "peer relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    ),
    ...props,
    ref,
    children: /* @__PURE__ */ jsx28(
      SwitchPrimitives.Thumb,
      {
        className: cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;

// src/components/table.tsx
import * as React17 from "react";
import { jsx as jsx29 } from "react/jsx-runtime";
var Table = React17.forwardRef(
  ({ className, ...props }, ref) => (
    // Styling ::-webkit-scrollbar opts out of macOS overlay scrollbars,
    // so a wide table shows a persistent thin bar instead of clipping
    // its last columns with no visible way to reach them.
    /* @__PURE__ */ jsx29("div", { className: "relative w-full overflow-auto [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent", children: /* @__PURE__ */ jsx29(
      "table",
      {
        ref,
        className: cn("w-full caption-bottom text-sm", className),
        ...props
      }
    ) })
  )
);
Table.displayName = "Table";
var TableHeader = React17.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx29("thead", { ref, className: cn("[&_tr]:border-b bg-muted/40", className), ...props })
);
TableHeader.displayName = "TableHeader";
var TableBody = React17.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx29(
    "tbody",
    {
      ref,
      className: cn("[&_tr:last-child]:border-0", className),
      ...props
    }
  )
);
TableBody.displayName = "TableBody";
var TableFooter = React17.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx29(
    "tfoot",
    {
      ref,
      className: cn(
        "border-t bg-muted/40 font-medium [&>tr]:last:border-b-0",
        className
      ),
      ...props
    }
  )
);
TableFooter.displayName = "TableFooter";
var TableRow = React17.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx29(
    "tr",
    {
      ref,
      className: cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className
      ),
      ...props
    }
  )
);
TableRow.displayName = "TableRow";
var TableHead = React17.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx29(
    "th",
    {
      ref,
      className: cn(
        "h-9 px-4 text-left align-middle text-xs font-medium uppercase tracking-wider text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className
      ),
      ...props
    }
  )
);
TableHead.displayName = "TableHead";
var TableCell = React17.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx29(
    "td",
    {
      ref,
      className: cn(
        "p-4 align-middle [&:has([role=checkbox])]:pr-0",
        className
      ),
      ...props
    }
  )
);
TableCell.displayName = "TableCell";
function TableBleed({
  className,
  children
}) {
  return /* @__PURE__ */ jsx29(
    "div",
    {
      className: cn(
        "-mx-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6 [&_td:first-child]:pl-6 [&_td:last-child]:pr-6",
        className
      ),
      children
    }
  );
}
var TableCaption = React17.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx29(
    "caption",
    {
      ref,
      className: cn("mt-4 text-sm text-muted-foreground", className),
      ...props
    }
  )
);
TableCaption.displayName = "TableCaption";

// src/components/tabs.tsx
import * as React18 from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { jsx as jsx30 } from "react/jsx-runtime";
var Tabs = TabsPrimitive.Root;
var TabsList = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx30(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "inline-flex h-11 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground sm:h-9",
      className
    ),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var TabsTrigger = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx30(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow sm:py-1",
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React18.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx30(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// src/components/textarea.tsx
import * as React19 from "react";
import { jsx as jsx31 } from "react/jsx-runtime";
var Textarea = React19.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx31(
      "textarea",
      {
        className: cn(
          "flex min-h-[80px] w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/30",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Textarea.displayName = "Textarea";

// src/components/tooltip.tsx
import * as React20 from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { jsx as jsx32 } from "react/jsx-runtime";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React20.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx32(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// src/components/stat-tile.tsx
import { jsx as jsx33, jsxs as jsxs19 } from "react/jsx-runtime";
var toneStyles = {
  default: "border-border",
  speed: "border-[hsl(var(--brand-accent)/0.4)]",
  alert: "border-[hsl(var(--triage-hot)/0.4)]",
  ai: "border-[hsl(var(--triage-ai)/0.4)]"
};
var deltaDirectionStyles = {
  up: "text-[hsl(var(--success))]",
  down: "text-[hsl(var(--triage-hot))]",
  flat: "text-muted-foreground"
};
var deltaArrows = {
  up: "\u25B2",
  down: "\u25BC",
  flat: "\u2014"
};
function StatTileCard({
  label,
  value,
  icon,
  tone = "default",
  delta,
  className
}) {
  return /* @__PURE__ */ jsxs19(
    "div",
    {
      className: cn(
        "flex h-full flex-col gap-3 rounded-lg border bg-card p-4 text-card-foreground min-h-[7.5rem]",
        toneStyles[tone],
        className
      ),
      children: [
        /* @__PURE__ */ jsxs19("div", { className: "flex items-start justify-between gap-2", children: [
          /* @__PURE__ */ jsx33("span", { className: "text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground", children: label }),
          icon && /* @__PURE__ */ jsx33("span", { className: "shrink-0 text-muted-foreground [&_svg]:h-4 [&_svg]:w-4", children: icon })
        ] }),
        /* @__PURE__ */ jsx33("span", { className: "font-mono text-2xl font-bold tabular-nums leading-none text-foreground", children: value }),
        /* @__PURE__ */ jsx33(
          "span",
          {
            className: cn(
              "mt-auto text-xs font-medium",
              delta ? deltaDirectionStyles[delta.direction] : "text-muted-foreground"
            ),
            "aria-hidden": !delta,
            children: delta ? `${deltaArrows[delta.direction]} ${delta.label}` : "\xA0"
          }
        )
      ]
    }
  );
}
function StatTile({ to, onClick, ...rest }) {
  if (to) {
    return /* @__PURE__ */ jsx33(
      "a",
      {
        href: to,
        onClick,
        className: "block h-full no-underline outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded-lg",
        children: /* @__PURE__ */ jsx33(StatTileCard, { ...rest })
      }
    );
  }
  return /* @__PURE__ */ jsx33(StatTileCard, { ...rest });
}

// src/components/triage-pill.tsx
import { jsx as jsx34 } from "react/jsx-runtime";
var variantStyles = {
  hot: "bg-[hsl(var(--triage-hot-bg))] text-[hsl(var(--triage-hot))]",
  // Darkened amber fg (--triage-warm-fg) for WCAG AA ≥4.5:1 on the warm background (§8.2).
  warm: "bg-[hsl(var(--triage-warm-bg))] text-[hsl(var(--triage-warm-fg))]",
  escalated: "bg-[hsl(var(--triage-hot-bg))] text-[hsl(var(--triage-hot))]",
  ai: "bg-[hsl(var(--triage-ai-bg))] text-[hsl(var(--triage-ai))]",
  cold: "bg-muted text-muted-foreground",
  // P3 "TODAY" tier — marine toned (draft 6 §3 Batch C).
  // Uses the secondary/accent marine-tint background with the primary marine foreground.
  p3: "bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]"
};
var defaultLabels = {
  hot: "Hot",
  warm: "Warm",
  cold: "Cold",
  ai: "AI active",
  escalated: "Escalated",
  p3: "P3 \xB7 Today"
};
function escalationTierToPillVariant(tier) {
  if (tier === "P1") return "hot";
  if (tier === "P2") return "warm";
  return "p3";
}
function TriagePill({ variant, children, className }) {
  return /* @__PURE__ */ jsx34(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none",
        variantStyles[variant],
        className
      ),
      children: children ?? defaultLabels[variant]
    }
  );
}

// src/components/sla-timer.tsx
import * as React21 from "react";
import { jsxs as jsxs20 } from "react/jsx-runtime";
var stateStyles = {
  fresh: "bg-[hsl(var(--triage-ai-bg))] text-[hsl(var(--triage-ai))]",
  // Darkened amber fg for WCAG AA ≥4.5:1 on the warm background (§8.2).
  warn: "bg-[hsl(var(--triage-warm-bg))] text-[hsl(var(--triage-warm-fg))]",
  breach: "bg-[hsl(var(--triage-hot-bg))] text-[hsl(var(--triage-hot))]"
};
function SlaTimer({ dueAt, now: nowProp, className }) {
  const [tick, setTick] = React21.useState(0);
  React21.useEffect(() => {
    if (!dueAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 1e3);
    return () => clearInterval(id);
  }, [dueAt]);
  if (!dueAt) return null;
  const now = nowProp ?? /* @__PURE__ */ new Date();
  const state = slaState(dueAt, now);
  const ms = msUntilDue(dueAt, now);
  void tick;
  return /* @__PURE__ */ jsxs20(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums leading-none",
        stateStyles[state],
        className
      ),
      children: [
        "\u26A1 ",
        formatMss(ms)
      ]
    }
  );
}

// src/components/score-gauge.tsx
import { jsx as jsx35, jsxs as jsxs21 } from "react/jsx-runtime";
var sizeConfig = {
  sm: { outer: 28, stroke: 4, fontSize: 9 },
  md: { outer: 64, stroke: 7, fontSize: 18 },
  lg: { outer: 96, stroke: 10, fontSize: 26 }
};
var bandColors = {
  cold: "hsl(var(--muted-foreground))",
  warm: "hsl(var(--triage-warm))",
  hot: "hsl(var(--triage-hot))"
};
function ScoreGauge({ score, size = "md", className }) {
  const clamped = clampScore(score);
  const band = scoreBand(clamped);
  const color = bandColors[band];
  const { outer, stroke, fontSize } = sizeConfig[size];
  const r = (outer - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const arcLength = circumference * 0.75;
  const filled = clamped / 100 * arcLength;
  const gap = arcLength - filled;
  const cx = outer / 2;
  const cy = outer / 2;
  return /* @__PURE__ */ jsxs21(
    "div",
    {
      className: cn("relative inline-flex items-center justify-center", className),
      style: { width: outer, height: outer },
      "aria-label": `Score ${clamped}`,
      children: [
        /* @__PURE__ */ jsxs21(
          "svg",
          {
            width: outer,
            height: outer,
            viewBox: `0 0 ${outer} ${outer}`,
            fill: "none",
            style: { transform: "rotate(135deg)" },
            children: [
              /* @__PURE__ */ jsx35(
                "circle",
                {
                  cx,
                  cy,
                  r,
                  stroke: "hsl(var(--border))",
                  strokeWidth: stroke,
                  strokeDasharray: `${arcLength} ${circumference - arcLength}`,
                  strokeLinecap: "round"
                }
              ),
              /* @__PURE__ */ jsx35(
                "circle",
                {
                  cx,
                  cy,
                  r,
                  stroke: color,
                  strokeWidth: stroke,
                  strokeDasharray: `${filled} ${gap + (circumference - arcLength)}`,
                  strokeLinecap: "round"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx35(
          "span",
          {
            className: "absolute font-mono font-bold tabular-nums",
            style: { fontSize, color, lineHeight: 1 },
            "aria-hidden": true,
            children: clamped
          }
        )
      ]
    }
  );
}

// src/components/quick-actions.tsx
import { jsx as jsx36 } from "react/jsx-runtime";
function QuickActions({ actions, className }) {
  return /* @__PURE__ */ jsx36("div", { className: cn("flex items-center gap-1", className), children: actions.map((action) => {
    const commonProps = {
      title: action.label,
      "aria-label": action.label,
      className: cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors",
        "text-muted-foreground hover:text-foreground hover:bg-muted",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
        action.accent && "text-[hsl(var(--brand-accent))] hover:text-[hsl(var(--brand-accent))] hover:bg-[hsl(var(--triage-warm-bg))]",
        "[&_svg]:h-4 [&_svg]:w-4"
      )
    };
    if (action.to) {
      const handleClick = action.onClick ? (e) => {
        e.preventDefault();
        action.onClick();
      } : void 0;
      return /* @__PURE__ */ jsx36("a", { href: action.to, onClick: handleClick, ...commonProps, children: action.icon }, action.key);
    }
    return /* @__PURE__ */ jsx36("button", { type: "button", onClick: action.onClick, ...commonProps, children: action.icon }, action.key);
  }) });
}

// src/components/funnel-bar.tsx
import { jsx as jsx37, jsxs as jsxs22 } from "react/jsx-runtime";
function FunnelBar({ stages, footer, className }) {
  const total = stages.reduce((sum, s) => sum + s.count, 0);
  return /* @__PURE__ */ jsxs22("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsx37("div", { className: "flex h-3 w-full overflow-hidden rounded-full bg-muted", children: stages.map((stage) => {
      const pct = total === 0 ? 0 : stage.count / total * 100;
      if (pct === 0) return null;
      return /* @__PURE__ */ jsx37(
        "div",
        {
          style: { width: `${pct}%`, background: stage.color },
          title: `${stage.label}: ${stage.count}`
        },
        stage.label
      );
    }) }),
    /* @__PURE__ */ jsx37("div", { className: "flex flex-col gap-1.5", children: stages.map((stage) => {
      const pct = total === 0 ? 0 : stage.count / total * 100;
      return /* @__PURE__ */ jsxs22("div", { className: "flex items-center gap-3 text-sm", children: [
        /* @__PURE__ */ jsx37(
          "span",
          {
            className: "h-2.5 w-2.5 shrink-0 rounded-full",
            style: { background: stage.color }
          }
        ),
        /* @__PURE__ */ jsx37("span", { className: "flex-1 text-foreground", children: stage.label }),
        /* @__PURE__ */ jsx37("span", { className: "tabular-nums text-foreground font-medium", children: stage.count }),
        /* @__PURE__ */ jsxs22("span", { className: "w-10 text-right tabular-nums text-muted-foreground text-xs", children: [
          pct.toFixed(0),
          "%"
        ] })
      ] }, stage.label);
    }) }),
    footer && /* @__PURE__ */ jsx37("div", { className: "text-xs text-muted-foreground", children: footer })
  ] });
}

// src/components/channel-count-chips.tsx
import { jsx as jsx38, jsxs as jsxs23 } from "react/jsx-runtime";
function ChannelCountChips({ counts, size = "sm", className }) {
  const channels = [
    { key: "voice", count: counts.phone },
    { key: "sms", count: counts.sms },
    { key: "email", count: counts.email },
    { key: "messenger", count: counts.social }
  ];
  const active = channels.filter((c) => c.count > 0);
  if (active.length === 0) return null;
  return /* @__PURE__ */ jsx38("span", { className: cn("inline-flex items-center gap-1.5", className), children: active.map(({ key, count }) => /* @__PURE__ */ jsxs23("span", { className: "inline-flex items-center gap-0.5", children: [
    /* @__PURE__ */ jsx38(ChannelIcon, { channel: key, size }),
    /* @__PURE__ */ jsx38("span", { className: "tabular-nums text-[10px] text-muted-foreground", children: count })
  ] }, key)) });
}

// src/components/status-pill.tsx
import { jsx as jsx39 } from "react/jsx-runtime";
var TONE_CLASSES = {
  default: "bg-primary/15 text-primary",
  success: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]",
  warning: "bg-[hsl(var(--triage-warm-bg))] text-[hsl(var(--triage-warm-fg))]",
  danger: "bg-[hsl(var(--triage-hot-bg))] text-[hsl(var(--triage-hot))]",
  muted: "bg-muted text-muted-foreground"
};
function StatusPill({ status, label, size = "md", className }) {
  const tone = LEAD_STATUS_COLOR_KEY[status] ?? "muted";
  const displayLabel = label ?? status;
  return /* @__PURE__ */ jsx39(
    "span",
    {
      className: cn(
        "inline-flex items-center rounded-full font-semibold leading-none",
        size === "sm" ? "px-1.5 py-px text-[10px]" : "px-2 py-0.5 text-[11px]",
        TONE_CLASSES[tone],
        className
      ),
      children: displayLabel
    }
  );
}

// src/components/bar-chart.tsx
import { jsx as jsx40, jsxs as jsxs24 } from "react/jsx-runtime";
function BarChart({
  data,
  formatValue = (n) => String(n),
  emptyText = "No data in this range."
}) {
  const max = Math.max(0, ...data.map((d) => d.value));
  if (data.length === 0 || max === 0) {
    return /* @__PURE__ */ jsx40("p", { className: "text-sm text-muted-foreground", children: emptyText });
  }
  const labelEvery = Math.max(1, Math.ceil(data.length / 6));
  return /* @__PURE__ */ jsxs24("div", { className: "flex flex-col gap-1.5", children: [
    /* @__PURE__ */ jsx40("div", { className: "flex h-40 items-end gap-0.5", children: data.map((d, i) => /* @__PURE__ */ jsx40(
      "div",
      {
        className: "flex-1 rounded-sm bg-primary/80 transition-colors hover:bg-primary",
        style: {
          // A non-zero value always shows at least a sliver so a
          // small-but-real bar is never invisible.
          height: `${d.value > 0 ? Math.max(d.value / max * 100, 3) : 0}%`
        },
        title: `${d.label}: ${formatValue(d.value)}`
      },
      `${d.label}-${i}`
    )) }),
    /* @__PURE__ */ jsx40("div", { className: "flex gap-0.5 text-[10px] text-muted-foreground", children: data.map((d, i) => /* @__PURE__ */ jsx40("div", { className: "flex-1 truncate text-center", children: i % labelEvery === 0 ? d.label : "" }, `${d.label}-${i}`)) })
  ] });
}
function BreakdownBars({
  data,
  formatValue = (n) => String(n),
  emptyText = "Nothing to show."
}) {
  const rows = data.filter((d) => d.value > 0).sort((a, b) => b.value - a.value);
  if (rows.length === 0) {
    return /* @__PURE__ */ jsx40("p", { className: "text-sm text-muted-foreground", children: emptyText });
  }
  const max = Math.max(...rows.map((d) => d.value));
  const total = rows.reduce((sum, d) => sum + d.value, 0);
  return /* @__PURE__ */ jsx40("div", { className: "flex flex-col gap-2", children: rows.map((d, i) => /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-3", children: [
    /* @__PURE__ */ jsx40("div", { className: "w-24 shrink-0 truncate text-sm sm:w-36", title: d.label, children: d.label }),
    /* @__PURE__ */ jsx40("div", { className: "h-5 flex-1 overflow-hidden rounded bg-muted", children: /* @__PURE__ */ jsx40(
      "div",
      {
        className: "h-full rounded bg-primary/80",
        style: { width: `${Math.max(d.value / max * 100, 2)}%` }
      }
    ) }),
    /* @__PURE__ */ jsxs24("div", { className: "w-16 shrink-0 text-right text-sm tabular-nums sm:w-28", children: [
      formatValue(d.value),
      total > 0 && /* @__PURE__ */ jsxs24("span", { className: "ml-1.5 text-xs text-muted-foreground", children: [
        Math.round(d.value / total * 100),
        "%"
      ] })
    ] })
  ] }, `${d.label}-${i}`)) });
}
export {
  APPOINTMENT_STATUS_COLOR_KEY,
  Badge,
  BarChart,
  BottomNav,
  BottomNavItem,
  BottomNavLabel,
  BottomNavSpacer,
  BreakdownBars,
  Button,
  Calendar,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ChannelCountChips,
  ChannelIcon,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  FACET_SECTIONS,
  FormField,
  FunnelBar,
  Input,
  InventorySearchToolbar,
  LEAD_STATUS_COLOR_KEY,
  Label,
  MobileAppBar,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  PhoneInput,
  QuickActions,
  SORT_OPTIONS,
  ScoreGauge,
  SearchInput,
  Section,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator2 as Separator,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  SlaTimer,
  StatTile,
  StatusPill,
  Switch,
  Table,
  TableBleed,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Toaster,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TriagePill,
  badgeVariants,
  buildInventorySearchRequest,
  buttonVariants,
  channelLabel,
  clampScore,
  cn,
  escalationTierToPillVariant,
  formatMss,
  formatPhoneDisplay,
  msUntilDue,
  nextPhoneValue,
  phoneInputToE164,
  scoreBand,
  slaState,
  toast,
  useZodForm
};
//# sourceMappingURL=index.js.map